import { useState, useCallback } from 'react';
import axios from 'axios';
import { get } from 'lodash';
import routes from '../../shared/routes';
import { LOGS_KEY } from '../../utils/constants';
import { logger } from '../../shared/logger';

const safeExecuteCallback = (callback, callbackName = 'callback', params) => {
    try {
        callback?.(params);
    } catch (err) {
        logger.error(err, {
            [LOGS_KEY]: `Error while executing ${callbackName} in PersonalizedSectionFetcher`,
        });
    }
};

const getRoute = ({ sectionName, useV5Routes }) =>
    useV5Routes ? routes.by_section.v5_fetcher(sectionName) : routes.by_section.fetcher(sectionName);

export const usePersonalizedData = ({
    appName,
    perseusName,
    onFetchSucceeded,
    onFetchFailed,
    recommendationsParams = {},
    useV5Routes = false,
    ...rest
}) => {
    const [personalizedData, setPersonalizedData] = useState<Record<string, any>>({});
    const [fetchStatus, setFetchStatus] = useState<{ isLoading: boolean; hasError?: boolean }>({
        isLoading: false,
        hasError: false,
    });

    const fetchPersonalizedData = useCallback(async (sectionName = null) => {
        setFetchStatus({ ...fetchStatus, isLoading: true });
        try {
            const fetchParams = sectionName
                ? {
                      ...recommendationsParams,
                      section_name: sectionName,
                  }
                : recommendationsParams;
            const requestPayload = { ...fetchParams, app_name: appName, perseus_name: perseusName };
            const url = getRoute({ sectionName: sectionName ?? rest.sectionName, useV5Routes });

            const response = await axios.post(url, requestPayload);

            if (!get(response.data, `sections.${sectionName ?? rest.sectionName}`)) {
                safeExecuteCallback(onFetchFailed, 'onFetchFailed', {
                    message: 'Fetched invalid data',
                    fetchedSections: Object.keys(get(response.data, 'sections', {})),
                });
                setFetchStatus({ isLoading: false, hasError: true });
            } else {
                safeExecuteCallback(onFetchSucceeded, 'onFetchSucceeded', response.data);
                setPersonalizedData(response.data);
                setFetchStatus({ isLoading: false });
            }
        } catch (err: any) {
            const { sectionName, personalizedContent, isLoading, isLazy, showTranslatedUGC, rollouts } = rest;

            if (![403].includes(err.response?.status)) {
                logger.error(err, {
                    [LOGS_KEY]: 'Error while fetching personalized content in CSR with ajax',
                    componentProps: JSON.stringify({
                        appName,
                        perseusName,
                        recommendationsParams,
                        sectionName,
                        personalizedContent,
                        isLazy,
                        isLoading,
                        showTranslatedUGC,
                        rollouts,
                    }),
                });
            }

            safeExecuteCallback(onFetchFailed, 'onFetchFailed', err);
            setFetchStatus({ isLoading: false, hasError: true });
        }

        // We can't put the relevant props in the depts array because their reference is always changed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { fetchPersonalizedData, personalizedData, fetchStatus };
};
